import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  Stack,
  Switch,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import {
  HTTP_CONFLICT,
  HTTP_UNPROCESSABLE_ENTITY,
  HttpError,
  type AutoApproveAction,
  type TrustRuleResponse,
} from "./api";
import { useCreateTrustRule } from "./useCreateTrustRule";
import { useUpdateTrustRule } from "./useUpdateTrustRule";

// Действия для checkbox-группы. Значения совпадают с backend-каноном
// (docs/spec/09-trust-control.md) — типобезопасность через AutoApproveAction.
const ACTION_OPTIONS: readonly AutoApproveAction[] = [
  "pr_merge",
  "dependency_update",
];

// Textarea → список glob'ов: строка-на-паттерн, обрезаем пробелы,
// выкидываем пустые строки. Зеркалит серверную валидацию (≥1 непустой).
function parsePaths(raw: string): string[] {
  return raw
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export interface TrustRuleFormModalProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  // null = режим create; объект = режим edit (префилл).
  readonly editing: TrustRuleResponse | null;
}

interface TrustRuleFormBodyProps {
  readonly onClose: () => void;
  readonly editing: TrustRuleResponse | null;
}

// Тело формы вынесено отдельным компонентом и монтируется только когда
// модалка открыта (см. `TrustRuleFormModal`). Поэтому начальное
// состояние берётся прямо из `editing` через useState-инициализатор —
// без useEffect-сброса (он триггерил бы cascading renders, eslint
// react-hooks/set-state-in-effect).
function TrustRuleFormBody({
  onClose,
  editing,
}: TrustRuleFormBodyProps): ReactElement {
  const { t } = useTranslation();
  const createMutation = useCreateTrustRule();
  const updateMutation = useUpdateTrustRule();

  const [name, setName] = useState(editing?.name ?? "");
  const [pathsText, setPathsText] = useState(
    editing !== null ? editing.paths_json.join("\n") : "",
  );
  const [actions, setActions] = useState<string[]>(
    editing !== null ? [...editing.auto_approve_json] : [],
  );
  const [isActive, setIsActive] = useState(editing?.is_active ?? true);
  const [formError, setFormError] = useState<string | null>(null);

  const errorMessage = (err: Error): string => {
    if (err instanceof HttpError) {
      if (err.status === HTTP_CONFLICT) {
        return t("trustRules.form.nameConflict");
      }
      if (err.status === HTTP_UNPROCESSABLE_ENTITY) {
        return t("trustRules.form.validationError");
      }
    }
    return t("trustRules.form.genericError", { message: err.message });
  };

  const handleSubmit = (): void => {
    const paths = parsePaths(pathsText);
    if (name.trim().length === 0) {
      setFormError(t("trustRules.form.nameRequired"));
      return;
    }
    if (paths.length === 0) {
      setFormError(t("trustRules.form.pathsRequired"));
      return;
    }
    setFormError(null);
    const autoApprove = actions as AutoApproveAction[];
    const trimmedName = name.trim();
    if (editing !== null) {
      updateMutation.mutate(
        {
          ruleId: editing.id,
          body: {
            name: trimmedName,
            paths,
            auto_approve: autoApprove,
            is_active: isActive,
          },
        },
        {
          onSuccess: () => {
            onClose();
          },
          onError: (err) => {
            setFormError(errorMessage(err));
          },
        },
      );
      return;
    }
    createMutation.mutate(
      { name: trimmedName, paths, auto_approve: autoApprove },
      {
        onSuccess: () => {
          onClose();
        },
        onError: (err) => {
          setFormError(errorMessage(err));
        },
      },
    );
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Stack gap="md">
      <TextInput
        label={t("trustRules.form.nameLabel")}
        placeholder={t("trustRules.form.namePlaceholder")}
        value={name}
        onChange={(event) => {
          setName(event.currentTarget.value);
        }}
      />
      <Textarea
        label={t("trustRules.form.pathsLabel")}
        description={t("trustRules.form.pathsHint")}
        placeholder={t("trustRules.form.pathsPlaceholder")}
        rows={4}
        value={pathsText}
        onChange={(event) => {
          setPathsText(event.currentTarget.value);
        }}
      />
      <Checkbox.Group
        label={t("trustRules.form.actionsLabel")}
        description={t("trustRules.form.actionsHint")}
        value={actions}
        onChange={setActions}
      >
        <Group mt="xs">
          {ACTION_OPTIONS.map((action) => (
            <Checkbox
              key={action}
              value={action}
              label={t(`trustRules.action.${action}`)}
            />
          ))}
        </Group>
      </Checkbox.Group>
      {editing !== null ? (
        <Switch
          label={t("trustRules.form.activeLabel")}
          checked={isActive}
          onChange={(event) => {
            setIsActive(event.currentTarget.checked);
          }}
        />
      ) : null}

      {formError !== null ? (
        <Alert color="red" title={t("trustRules.errorHeading")}>
          {formError}
        </Alert>
      ) : null}

      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          {t("trustRules.modalCancel")}
        </Button>
        <Button onClick={handleSubmit} loading={isPending}>
          {t("trustRules.form.submit")}
        </Button>
      </Group>
    </Stack>
  );
}

export function TrustRuleFormModal({
  opened,
  onClose,
  editing,
}: TrustRuleFormModalProps): ReactElement {
  const { t } = useTranslation();
  const title =
    editing !== null
      ? t("trustRules.form.editTitle")
      : t("trustRules.form.createTitle");

  return (
    <Modal opened={opened} onClose={onClose} title={title} centered>
      {opened ? (
        <TrustRuleFormBody
          key={editing?.id ?? "create"}
          onClose={onClose}
          editing={editing}
        />
      ) : null}
    </Modal>
  );
}
